// src/prototype/contexts/EAContext.tsx
// ============================================================
// EA PROTOTYPE CONTEXT v5
// UPDATED: Added categoryWeights and measureWeights for 
// ranking customization (Step 1 → Step 2 data flow)
// UPDATED v4: demoProUnlocked now reads from localStorage on init
//   and syncs with useDemoPlan via dva_demo_plan_change event.
//   Fixes Execute step routing: EAActivatePage no longer redirects
//   to /ea/upgrade when isPro is true in useDemoPlan.
// UPDATED v5: Evaluate state (shortlist, finalists, order, flagged
//   assumptions) persists to BigQuery via
//   GET/PUT /api/v2/use-case-explorer/:clientId/evaluate-state.
//   localStorage remains a cache / offline fallback.
//   Weights continue to use /api/v2/weights (unchanged).
//
// This context is isolated to the prototype and does not affect
// production entitlements or billing.
// ============================================================

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  CategoryWeightsMap,
  MeasureWeightsMap,
  getDefaultCategoryWeightsMap,
  getDefaultMeasureWeightsMap,
  rebalanceCategoryWeights,
  rebalanceMeasureWeights,
  areCategoryWeightsDefault,
} from '../utils/weighting';
import { EA_CLIENT_ID } from '../constants/eaClient';
import {
  getEvaluateState,
  saveEvaluateState,
  type EvaluateStatePayload,
} from '../services/useCaseExplorerService';

// Types for EA feedback collection
export type FlagType = 'incorrect' | 'missing' | 'too_aggressive' | 'too_conservative' | 'too_high' | 'too_low';

export type InitiativeShortlistBucket = 'launch' | 'pilot' | 'monitor' | 'defer';

export type InitiativeShortlistBuckets = Record<InitiativeShortlistBucket, Set<string>>;

const EMPTY_SHORTLIST_BUCKETS = (): InitiativeShortlistBuckets => ({
  launch: new Set(),
  pilot: new Set(),
  monitor: new Set(),
  defer: new Set(),
});

const SHORTLIST_BUCKET_KEYS: InitiativeShortlistBucket[] = ['launch', 'pilot', 'monitor', 'defer'];

export interface EAFlag {
  id: string;
  targetType: 'assumption' | 'context' | 'opportunity';
  targetId: string;
  flagType: FlagType;
  comment?: string;
  timestamp: Date;
}

export interface EAEdit {
  id: string;
  targetType: 'context';
  targetId: string;
  originalValue: string;
  newValue: string;
  timestamp: Date;
}

interface EAContextType {
  // EA Mode detection
  isEAMode: boolean;

  // Simulated Pro upgrade (session-scoped, resets on refresh)
  demoProUnlocked: boolean;
  setDemoProUnlocked: (val: boolean) => void;

  // Feedback collection (stored in memory for prototype)
  flags: EAFlag[];
  addFlag: (flag: Omit<EAFlag, 'id' | 'timestamp'>) => void;
  removeFlag: (id: string) => void;

  edits: EAEdit[];
  addEdit: (edit: Omit<EAEdit, 'id' | 'timestamp'>) => void;

  // ============================================================
  // WEIGHTING CONFIGURATION (Step 1 → Step 2)
  // ============================================================

  // Category weights (6 categories, sum to 100%)
  categoryWeights: CategoryWeightsMap;
  setCategoryWeights: (weights: CategoryWeightsMap) => void;
  updateCategoryWeight: (key: string, value: number) => void;
  resetCategoryWeights: () => void;
  areCategoryWeightsCustomized: boolean;

  // Measure weights (per-category, each sums to 100%)
  measureWeights: MeasureWeightsMap;
  setMeasureWeights: (weights: MeasureWeightsMap) => void;
  updateMeasureWeight: (categoryKey: string, measureKey: string, value: number) => void;
  resetMeasureWeights: (categoryKey?: string) => void;
  resetAllMeasureWeights: () => void;
  areMeasureWeightsCustomized: boolean;

  // ============================================================
  // FINALIST MANAGEMENT (Step 2 → Step 3)
  // ============================================================

  // Selected finalists (managed in Step 2)
  finalistIds: Set<string>;
  setFinalistIds: (ids: Set<string>) => void;
  addFinalist: (id: string) => void;
  removeFinalist: (id: string) => void;
  toggleFinalist: (id: string) => void;

  // Priority ordering (can be reordered in Step 3)
  finalistOrder: string[];
  setFinalistOrder: (order: string[]) => void;
  moveFinalistUp: (id: string) => void;
  moveFinalistDown: (id: string) => void;

  // Flagged assumptions from Step 2 (read in Step 3)
  flaggedAssumptions: Map<string, FlagType>;
  setFlaggedAssumptions: (flags: Map<string, FlagType>) => void;
  setFlaggedAssumption: (oppId: string, assumptionId: string, flagType: FlagType | null) => void;

  // Initiative shortlist (Strategy Decision Canvas — execution posture assignments)
  initiativeShortlistBuckets: InitiativeShortlistBuckets;
  initiativeShortlistIds: Set<string>;
  initiativeShortlistCount: number;
  assignInitiativeToShortlist: (id: string, bucket: InitiativeShortlistBucket) => void;
  unassignInitiativeFromShortlist: (id: string) => void;
  clearInitiativeShortlist: () => void;

  // Helper to check if user can access Pro steps
  canAccessProSteps: boolean;
}

const EAContext = createContext<EAContextType | undefined>(undefined);

// LocalStorage keys — cache / offline fallback (DB is source of truth when available)
const STORAGE_KEYS = {
  FINALIST_IDS: 'ea_finalist_ids',
  FINALIST_ORDER: 'ea_finalist_order',
  FLAGGED_ASSUMPTIONS: 'ea_flagged_assumptions',
  CATEGORY_WEIGHTS: 'ea_category_weights',
  MEASURE_WEIGHTS: 'ea_measure_weights',
  INITIATIVE_SHORTLIST: 'ea_initiative_shortlist',
};

const EVALUATE_STATE_DEBOUNCE_MS = 500;

function shortlistBucketsToPayload(buckets: InitiativeShortlistBuckets): EvaluateStatePayload['initiative_shortlist'] {
  return {
    launch: Array.from(buckets.launch),
    pilot: Array.from(buckets.pilot),
    monitor: Array.from(buckets.monitor),
    defer: Array.from(buckets.defer),
  };
}

function payloadToShortlistBuckets(
  shortlist: EvaluateStatePayload['initiative_shortlist'] | undefined,
): InitiativeShortlistBuckets {
  return {
    launch: new Set(shortlist?.launch ?? []),
    pilot: new Set(shortlist?.pilot ?? []),
    monitor: new Set(shortlist?.monitor ?? []),
    defer: new Set(shortlist?.defer ?? []),
  };
}

function loadInitiativeShortlistBuckets(): InitiativeShortlistBuckets {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.INITIATIVE_SHORTLIST);
    if (!stored) return EMPTY_SHORTLIST_BUCKETS();

    const parsed = JSON.parse(stored) as Partial<Record<InitiativeShortlistBucket, string[]>>;
    return {
      launch: new Set(parsed.launch ?? []),
      pilot: new Set(parsed.pilot ?? []),
      monitor: new Set(parsed.monitor ?? []),
      defer: new Set(parsed.defer ?? []),
    };
  } catch (e) {
    console.warn('[EA] Failed to load initiative shortlist from localStorage:', e);
    return EMPTY_SHORTLIST_BUCKETS();
  }
}

function persistInitiativeShortlistBuckets(buckets: InitiativeShortlistBuckets) {
  try {
    localStorage.setItem(
      STORAGE_KEYS.INITIATIVE_SHORTLIST,
      JSON.stringify(shortlistBucketsToPayload(buckets)),
    );
  } catch (e) {
    console.warn('[EA] Failed to save initiative shortlist:', e);
  }
}

function persistLocalFinalistIds(ids: Set<string>) {
  try {
    localStorage.setItem(STORAGE_KEYS.FINALIST_IDS, JSON.stringify(Array.from(ids)));
  } catch (e) {
    console.warn('[EA] Failed to save finalist IDs:', e);
  }
}

function persistLocalFinalistOrder(order: string[]) {
  try {
    localStorage.setItem(STORAGE_KEYS.FINALIST_ORDER, JSON.stringify(order));
  } catch (e) {
    console.warn('[EA] Failed to save finalist order:', e);
  }
}

function persistLocalFlaggedAssumptions(flags: Map<string, FlagType>) {
  try {
    localStorage.setItem(
      STORAGE_KEYS.FLAGGED_ASSUMPTIONS,
      JSON.stringify(Array.from(flags.entries())),
    );
  } catch (e) {
    console.warn('[EA] Failed to save flagged assumptions:', e);
  }
}

function buildEvaluateStatePayload(
  finalistIds: Set<string>,
  finalistOrder: string[],
  buckets: InitiativeShortlistBuckets,
  flagged: Map<string, FlagType>,
): EvaluateStatePayload {
  return {
    finalist_ids: Array.from(finalistIds, (id) => String(id)),
    finalist_order: finalistOrder.map(String),
    initiative_shortlist: shortlistBucketsToPayload(buckets),
    flagged_assumptions: Array.from(flagged.entries()),
  };
}

function evaluateStateHasContent(state: EvaluateStatePayload): boolean {
  return (
    state.finalist_ids.length > 0 ||
    state.finalist_order.length > 0 ||
    state.flagged_assumptions.length > 0 ||
    SHORTLIST_BUCKET_KEYS.some((key) => state.initiative_shortlist[key].length > 0)
  );
}

// Must match the key used in useDemoPlan.tsx
const DEMO_PLAN_KEY = 'dva_demo_plan_override';
const DEMO_PLAN_CHANGE_EVENT = 'dva_demo_plan_change';

interface EAProviderProps {
  children: React.ReactNode;
}

export function EAProvider({ children }: EAProviderProps) {
  // Simulated upgrade state — initialized from localStorage so it stays
  // in sync with useDemoPlan (which also reads/writes dva_demo_plan_override).
  const [demoProUnlocked, setDemoProUnlocked] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DEMO_PLAN_KEY) === 'pro';
    } catch {
      return false;
    }
  });

  // Stay in sync when useDemoPlan.upgrade() / reset() fires the custom event
  useEffect(() => {
    const handlePlanChange = (e: CustomEvent<string | null>) => {
      const isPro = e.detail === 'pro';
      console.log('[EAContext] dva_demo_plan_change received — demoProUnlocked →', isPro);
      setDemoProUnlocked(isPro);
    };
    window.addEventListener(DEMO_PLAN_CHANGE_EVENT, handlePlanChange as EventListener);
    return () => window.removeEventListener(DEMO_PLAN_CHANGE_EVENT, handlePlanChange as EventListener);
  }, []);

  // Feedback collection - in-memory for prototype
  const [flags, setFlags] = useState<EAFlag[]>([]);
  const [edits, setEdits] = useState<EAEdit[]>([]);

  // ============================================================
  // WEIGHTING STATE (persisted to localStorage)
  // ============================================================

  const [categoryWeights, setCategoryWeightsState] = useState<CategoryWeightsMap>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.CATEGORY_WEIGHTS);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Validate the stored weights have expected keys
        if (typeof parsed === 'object' && 'costSavings' in parsed && 'strategicAlignment' in parsed) {
          return parsed as CategoryWeightsMap;
        }
      }
    } catch (e) {
      console.warn('[EA] Failed to load category weights from localStorage:', e);
    }
    return getDefaultCategoryWeightsMap();
  });

  const [measureWeights, setMeasureWeightsState] = useState<MeasureWeightsMap>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.MEASURE_WEIGHTS);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (typeof parsed === 'object' && 'costSavings' in parsed) {
          return parsed as MeasureWeightsMap;
        }
      }
    } catch (e) {
      console.warn('[EA] Failed to load measure weights from localStorage:', e);
    }
    return getDefaultMeasureWeightsMap();
  });

  // ============================================================
  // FINALIST STATE (persisted to localStorage)
  // ============================================================

  const [finalistIds, setFinalistIdsState] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.FINALIST_IDS);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return new Set(parsed.map((id) => String(id)));
        }
      }
    } catch (e) {
      console.warn('[EA] Failed to load finalist IDs from localStorage:', e);
    }
    return new Set();
  });

  const [finalistOrder, setFinalistOrderState] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.FINALIST_ORDER);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.warn('[EA] Failed to load finalist order from localStorage:', e);
    }
    return [];
  });

  const [initiativeShortlistBuckets, setInitiativeShortlistBucketsState] = useState<InitiativeShortlistBuckets>(
    loadInitiativeShortlistBuckets,
  );

  const initiativeShortlistIds = useMemo(() => {
    const ids = new Set<string>();
    SHORTLIST_BUCKET_KEYS.forEach((key) => {
      initiativeShortlistBuckets[key].forEach((id) => ids.add(id));
    });
    return ids;
  }, [initiativeShortlistBuckets]);

  const initiativeShortlistCount = initiativeShortlistIds.size;

  const [flaggedAssumptions, setFlaggedAssumptionsState] = useState<Map<string, FlagType>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.FLAGGED_ASSUMPTIONS);
      if (stored) {
        return new Map(JSON.parse(stored));
      }
    } catch (e) {
      console.warn('[EA] Failed to load flagged assumptions from localStorage:', e);
    }
    return new Map();
  });

  // Keep latest evaluate snapshot in a ref so debounced DB writes never go stale.
  const evaluateSnapshotRef = useRef<EvaluateStatePayload>(
    buildEvaluateStatePayload(
      finalistIds,
      finalistOrder,
      initiativeShortlistBuckets,
      flaggedAssumptions,
    ),
  );
  const dbPersistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipDbPersistRef = useRef(false);
  const evaluateStateHydratedRef = useRef(false);

  useEffect(() => {
    evaluateSnapshotRef.current = buildEvaluateStatePayload(
      finalistIds,
      finalistOrder,
      initiativeShortlistBuckets,
      flaggedAssumptions,
    );
  }, [finalistIds, finalistOrder, initiativeShortlistBuckets, flaggedAssumptions]);

  const flushEvaluateStateToDb = useCallback(async (payload?: EvaluateStatePayload) => {
    const state = payload ?? evaluateSnapshotRef.current;
    try {
      await saveEvaluateState(EA_CLIENT_ID, state);
      console.log('[EA] evaluate-state saved to DB');
    } catch (e) {
      console.warn('[EA] evaluate-state DB save failed (local cache kept):', e);
    }
  }, []);

  const scheduleEvaluateStateDbPersist = useCallback((patch?: Partial<EvaluateStatePayload>) => {
    if (skipDbPersistRef.current) return;
    if (patch) {
      evaluateSnapshotRef.current = {
        ...evaluateSnapshotRef.current,
        ...patch,
        initiative_shortlist: patch.initiative_shortlist
          ?? evaluateSnapshotRef.current.initiative_shortlist,
      };
    }
    if (dbPersistTimerRef.current) clearTimeout(dbPersistTimerRef.current);
    dbPersistTimerRef.current = setTimeout(() => {
      dbPersistTimerRef.current = null;
      void flushEvaluateStateToDb();
    }, EVALUATE_STATE_DEBOUNCE_MS);
  }, [flushEvaluateStateToDb]);

  const applyEvaluateStateFromDb = useCallback((state: EvaluateStatePayload) => {
    skipDbPersistRef.current = true;
    try {
      const ids = new Set((state.finalist_ids || []).map(String));
      const order = (state.finalist_order || []).map(String);
      const buckets = payloadToShortlistBuckets(state.initiative_shortlist);
      const flags = new Map<string, FlagType>(
        (state.flagged_assumptions || [])
          .filter((entry): entry is [string, FlagType] => Array.isArray(entry) && entry.length >= 2)
          .map(([key, flag]) => [String(key), flag as FlagType]),
      );

      setFinalistIdsState(ids);
      persistLocalFinalistIds(ids);
      setFinalistOrderState(order);
      persistLocalFinalistOrder(order);
      setInitiativeShortlistBucketsState(buckets);
      persistInitiativeShortlistBuckets(buckets);
      setFlaggedAssumptionsState(flags);
      persistLocalFlaggedAssumptions(flags);
      evaluateSnapshotRef.current = buildEvaluateStatePayload(ids, order, buckets, flags);
      console.log('[EA] evaluate-state hydrated from DB', {
        finalists: ids.size,
        shortlist: SHORTLIST_BUCKET_KEYS.reduce(
          (n, key) => n + buckets[key].size,
          0,
        ),
        flags: flags.size,
      });
    } finally {
      // Allow subsequent user edits to persist
      setTimeout(() => {
        skipDbPersistRef.current = false;
      }, 0);
    }
  }, []);

  // Hydrate from DB on mount; migrate localStorage → DB when server has no row yet.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await getEvaluateState(EA_CLIENT_ID);
        if (cancelled) return;

        if (res.exists && evaluateStateHasContent(res.state)) {
          applyEvaluateStateFromDb(res.state);
        } else {
          const local = evaluateSnapshotRef.current;
          if (evaluateStateHasContent(local)) {
            console.log('[EA] Migrating local evaluate-state → DB');
            await flushEvaluateStateToDb(local);
          }
        }
      } catch (e) {
        console.warn('[EA] evaluate-state hydrate failed — using localStorage cache:', e);
      } finally {
        if (!cancelled) {
          evaluateStateHydratedRef.current = true;
          console.log('[EA] evaluate-state hydration complete');
        }
      }
    })();

    return () => {
      cancelled = true;
      if (dbPersistTimerRef.current) clearTimeout(dbPersistTimerRef.current);
    };
  }, [applyEvaluateStateFromDb, flushEvaluateStateToDb]);

  // EA mode is determined by route prefix
  const isEAMode = typeof window !== 'undefined'
    && window.location.pathname.startsWith('/ea');

  // ============================================================
  // CATEGORY WEIGHT MANAGEMENT
  // ============================================================

  const updateCategoryWeight = useCallback((key: string, value: number) => {
    setCategoryWeightsState(prev => {
      const newWeights = rebalanceCategoryWeights(prev, key, value);
      try {
        localStorage.setItem(STORAGE_KEYS.CATEGORY_WEIGHTS, JSON.stringify(newWeights));
      } catch (e) {
        console.warn('[EA] Failed to save category weights:', e);
      }
      console.log(`⚖️ Category weight updated: ${key} → ${value}%`, newWeights);
      return newWeights;
    });
  }, []);

  const setCategoryWeights = useCallback((weights: CategoryWeightsMap) => {
    setCategoryWeightsState(weights);
    try {
      localStorage.setItem(STORAGE_KEYS.CATEGORY_WEIGHTS, JSON.stringify(weights));
    } catch (e) {
      console.warn('[EA] Failed to save category weights:', e);
    }
    console.log('⚖️ Category weights loaded from API');
  }, []);

  const resetCategoryWeights = useCallback(() => {
    const defaults = getDefaultCategoryWeightsMap();
    setCategoryWeightsState(defaults);
    try {
      localStorage.setItem(STORAGE_KEYS.CATEGORY_WEIGHTS, JSON.stringify(defaults));
    } catch (e) {
      console.warn('[EA] Failed to save category weights:', e);
    }
    console.log('⚖️ Category weights reset to defaults');
  }, []);

  const areCategoryWeightsCustomized = useMemo(() => {
    return !areCategoryWeightsDefault(categoryWeights);
  }, [categoryWeights]);

  // ============================================================
  // MEASURE WEIGHT MANAGEMENT
  // ============================================================

  const updateMeasureWeight = useCallback((categoryKey: string, measureKey: string, value: number) => {
    setMeasureWeightsState(prev => {
      const categoryMeasures = prev[categoryKey] || {};
      const newCategoryMeasures = rebalanceMeasureWeights(categoryMeasures, measureKey, value);
      const newWeights = { ...prev, [categoryKey]: newCategoryMeasures };

      try {
        localStorage.setItem(STORAGE_KEYS.MEASURE_WEIGHTS, JSON.stringify(newWeights));
      } catch (e) {
        console.warn('[EA] Failed to save measure weights:', e);
      }
      console.log(`📊 Measure weight updated: ${categoryKey}.${measureKey} → ${value}%`);
      return newWeights;
    });
  }, []);

  const setMeasureWeights = useCallback((weights: MeasureWeightsMap) => {
    setMeasureWeightsState(weights);
    try {
      localStorage.setItem(STORAGE_KEYS.MEASURE_WEIGHTS, JSON.stringify(weights));
    } catch (e) {
      console.warn('[EA] Failed to save measure weights:', e);
    }
    console.log('📊 Measure weights loaded from API');
  }, []);

  const resetMeasureWeights = useCallback((categoryKey?: string) => {
    setMeasureWeightsState(prev => {
      const defaults = getDefaultMeasureWeightsMap();
      let newWeights: MeasureWeightsMap;

      if (categoryKey) {
        // Reset only one category
        newWeights = { ...prev, [categoryKey]: defaults[categoryKey] };
        console.log(`📊 Measure weights reset for category: ${categoryKey}`);
      } else {
        // Reset all
        newWeights = defaults;
        console.log('📊 All measure weights reset to defaults');
      }

      try {
        localStorage.setItem(STORAGE_KEYS.MEASURE_WEIGHTS, JSON.stringify(newWeights));
      } catch (e) {
        console.warn('[EA] Failed to save measure weights:', e);
      }
      return newWeights;
    });
  }, []);

  const resetAllMeasureWeights = useCallback(() => {
    resetMeasureWeights();
  }, [resetMeasureWeights]);

  // Check if any measure weights are customized
  const areMeasureWeightsCustomized = useMemo(() => {
    const defaults = getDefaultMeasureWeightsMap();
    return JSON.stringify(measureWeights) !== JSON.stringify(defaults);
  }, [measureWeights]);

  // ============================================================
  // FINALIST ID MANAGEMENT
  // ============================================================

  const setFinalistIds = useCallback((ids: Set<string>) => {
    const normalized = new Set(Array.from(ids, (id) => String(id)));
    setFinalistIdsState(normalized);
    persistLocalFinalistIds(normalized);
    scheduleEvaluateStateDbPersist({ finalist_ids: Array.from(normalized) });
    console.log('⭐ Finalists updated:', Array.from(normalized));
  }, [scheduleEvaluateStateDbPersist]);

  const addFinalist = useCallback((id: string) => {
    const sid = String(id);
    setFinalistIdsState(prev => {
      const next = new Set(Array.from(prev, (existing) => String(existing)));
      next.add(sid);
      persistLocalFinalistIds(next);
      scheduleEvaluateStateDbPersist({ finalist_ids: Array.from(next) });
      console.log('⭐ Finalist added:', id);
      return next;
    });
  }, [scheduleEvaluateStateDbPersist]);

  const removeFinalist = useCallback((id: string) => {
    const sid = String(id);
    setFinalistIdsState(prev => {
      const next = new Set(Array.from(prev, (existing) => String(existing)));
      next.delete(sid);
      persistLocalFinalistIds(next);
      scheduleEvaluateStateDbPersist({ finalist_ids: Array.from(next) });
      console.log('⭐ Finalist removed:', id);
      return next;
    });
  }, [scheduleEvaluateStateDbPersist]);

  const toggleFinalist = useCallback((id: string) => {
    const sid = String(id);
    setFinalistIdsState(prev => {
      const next = new Set(Array.from(prev, (existing) => String(existing)));
      if (next.has(sid)) {
        next.delete(sid);
        console.log('⭐ Finalist removed:', sid);
      } else {
        next.add(sid);
        console.log('⭐ Finalist added:', sid);
      }
      persistLocalFinalistIds(next);
      scheduleEvaluateStateDbPersist({ finalist_ids: Array.from(next) });
      return next;
    });
  }, [scheduleEvaluateStateDbPersist]);

  // ============================================================
  // INITIATIVE SHORTLIST (Strategy Decision Canvas)
  // ============================================================

  const assignInitiativeToShortlist = useCallback((id: string, bucket: InitiativeShortlistBucket) => {
    setInitiativeShortlistBucketsState((prev) => {
      const next = EMPTY_SHORTLIST_BUCKETS();
      SHORTLIST_BUCKET_KEYS.forEach((key) => {
        prev[key].forEach((existingId) => {
          if (existingId !== id) next[key].add(existingId);
        });
      });
      next[bucket].add(id);
      persistInitiativeShortlistBuckets(next);
      scheduleEvaluateStateDbPersist({
        initiative_shortlist: shortlistBucketsToPayload(next),
      });
      return next;
    });
  }, [scheduleEvaluateStateDbPersist]);

  const unassignInitiativeFromShortlist = useCallback((id: string) => {
    setInitiativeShortlistBucketsState((prev) => {
      const next = EMPTY_SHORTLIST_BUCKETS();
      SHORTLIST_BUCKET_KEYS.forEach((key) => {
        prev[key].forEach((existingId) => {
          if (existingId !== id) next[key].add(existingId);
        });
      });
      persistInitiativeShortlistBuckets(next);
      scheduleEvaluateStateDbPersist({
        initiative_shortlist: shortlistBucketsToPayload(next),
      });
      return next;
    });
  }, [scheduleEvaluateStateDbPersist]);

  const clearInitiativeShortlist = useCallback(() => {
    const next = EMPTY_SHORTLIST_BUCKETS();
    setInitiativeShortlistBucketsState(next);
    persistInitiativeShortlistBuckets(next);
    scheduleEvaluateStateDbPersist({
      initiative_shortlist: shortlistBucketsToPayload(next),
    });
  }, [scheduleEvaluateStateDbPersist]);

  // ============================================================
  // FINALIST ORDER MANAGEMENT
  // ============================================================

  const setFinalistOrder = useCallback((order: string[]) => {
    setFinalistOrderState(order);
    persistLocalFinalistOrder(order);
    scheduleEvaluateStateDbPersist({ finalist_order: order.map(String) });
    console.log('📋 Finalist order updated:', order);
  }, [scheduleEvaluateStateDbPersist]);

  const moveFinalistUp = useCallback((id: string) => {
    setFinalistOrderState(prev => {
      const index = prev.indexOf(id);
      if (index <= 0) return prev;

      const newOrder = [...prev];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];

      persistLocalFinalistOrder(newOrder);
      scheduleEvaluateStateDbPersist({ finalist_order: newOrder.map(String) });
      console.log('⬆️ Moved finalist up:', id);
      return newOrder;
    });
  }, [scheduleEvaluateStateDbPersist]);

  const moveFinalistDown = useCallback((id: string) => {
    setFinalistOrderState(prev => {
      const index = prev.indexOf(id);
      if (index === -1 || index >= prev.length - 1) return prev;

      const newOrder = [...prev];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];

      persistLocalFinalistOrder(newOrder);
      scheduleEvaluateStateDbPersist({ finalist_order: newOrder.map(String) });
      console.log('⬇️ Moved finalist down:', id);
      return newOrder;
    });
  }, [scheduleEvaluateStateDbPersist]);

  // Sync finalistOrder when finalistIds changes
  useEffect(() => {
    const idsArray = Array.from(finalistIds, (id) => String(id));
    const currentOrderSet = new Set(finalistOrder.map((id) => String(id)));
    const finalistIdSet = new Set(idsArray);

    const newIds = idsArray.filter(id => !currentOrderSet.has(id));
    const validOrder = finalistOrder.map((id) => String(id)).filter(id => finalistIdSet.has(id));

    // Combine: existing valid order + new IDs
    const newOrder = [...validOrder, ...newIds];

    // Only update if there's a change
    if (newOrder.length !== finalistOrder.length ||
      newOrder.some((id, i) => finalistOrder[i] !== id)) {
      setFinalistOrder(newOrder);
    }
  }, [finalistIds, finalistOrder, setFinalistOrder]);

  // ============================================================
  // FLAGGED ASSUMPTIONS MANAGEMENT
  // ============================================================

  const setFlaggedAssumptions = useCallback((flags: Map<string, FlagType>) => {
    setFlaggedAssumptionsState(flags);
    persistLocalFlaggedAssumptions(flags);
    scheduleEvaluateStateDbPersist({
      flagged_assumptions: Array.from(flags.entries()),
    });
  }, [scheduleEvaluateStateDbPersist]);

  const setFlaggedAssumption = useCallback((oppId: string, assumptionId: string, flagType: FlagType | null) => {
    const key = `${oppId}-${assumptionId}`;
    setFlaggedAssumptionsState(prev => {
      const next = new Map(prev);
      if (flagType === null) {
        next.delete(key);
        console.log('🚩 Assumption flag removed:', key);
      } else {
        next.set(key, flagType);
        console.log('🚩 Assumption flagged:', key, flagType);
      }
      persistLocalFlaggedAssumptions(next);
      scheduleEvaluateStateDbPersist({
        flagged_assumptions: Array.from(next.entries()),
      });
      return next;
    });
  }, [scheduleEvaluateStateDbPersist]);

  // ============================================================
  // LEGACY FLAG/EDIT MANAGEMENT
  // ============================================================

  const addFlag = useCallback((flag: Omit<EAFlag, 'id' | 'timestamp'>) => {
    const newFlag: EAFlag = {
      ...flag,
      id: `flag-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
    };
    setFlags(prev => [...prev, newFlag]);
    console.log('🚩 EA Flag added:', newFlag);
  }, []);

  const removeFlag = useCallback((id: string) => {
    setFlags(prev => prev.filter(f => f.id !== id));
    console.log('🚩 EA Flag removed:', id);
  }, []);

  const addEdit = useCallback((edit: Omit<EAEdit, 'id' | 'timestamp'>) => {
    const newEdit: EAEdit = {
      ...edit,
      id: `edit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
    };
    setEdits(prev => [...prev, newEdit]);
    console.log('✏️ EA Edit added:', newEdit);
  }, []);

  // ============================================================
  // DERIVED STATE
  // ============================================================

  const canAccessProSteps = demoProUnlocked;

  // ============================================================
  // CONTEXT VALUE
  // ============================================================

  const value: EAContextType = {
    isEAMode,
    demoProUnlocked,
    setDemoProUnlocked,
    flags,
    addFlag,
    removeFlag,
    edits,
    addEdit,
    // Weighting
    categoryWeights,
    setCategoryWeights,
    updateCategoryWeight,
    resetCategoryWeights,
    areCategoryWeightsCustomized,
    measureWeights,
    setMeasureWeights,
    updateMeasureWeight,
    resetMeasureWeights,
    resetAllMeasureWeights,
    areMeasureWeightsCustomized,
    // Finalists
    finalistIds,
    setFinalistIds,
    addFinalist,
    removeFinalist,
    toggleFinalist,
    finalistOrder,
    setFinalistOrder,
    moveFinalistUp,
    moveFinalistDown,
    flaggedAssumptions,
    setFlaggedAssumptions,
    setFlaggedAssumption,
    initiativeShortlistBuckets,
    initiativeShortlistIds,
    initiativeShortlistCount,
    assignInitiativeToShortlist,
    unassignInitiativeFromShortlist,
    clearInitiativeShortlist,
    canAccessProSteps,
  };

  return (
    <EAContext.Provider value={value}>
      {children}
    </EAContext.Provider>
  );
}

export function useEA(): EAContextType {
  const context = useContext(EAContext);
  if (!context) {
    throw new Error('useEA must be used within an EAProvider');
  }
  return context;
}

export function useEAOptional(): EAContextType | null {
  return useContext(EAContext) || null;
}